import {inject, injectable} from 'inversify';

import type {CardApiModel} from '../../../../api/payment';
import type {IApiDataMapper} from '../../../../core/ports/types';

import {DateTimeMapperJrpc} from '../../Date/mappers/DateTime.mapper.jrpc';
import {CardFactory} from '../Card.factory';
import {CardModel} from '../Card.model';
import {CardExpandJrpc} from './Card.expand.jrpc';

@injectable()
export class CardMapperJrpc
  implements IApiDataMapper<CardMapperJrpc.ApiData, CardModel.Props, CardModel>
{
  constructor(
    @inject(CardFactory)
    private readonly factory: CardFactory,
    @inject(CardExpandJrpc)
    public readonly expand: CardExpandJrpc,
    @inject(DateTimeMapperJrpc)
    private readonly dateTimeMapper: DateTimeMapperJrpc,
  ) {}

  mapDataToModel(apiData: CardMapperJrpc.ApiData): CardModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToProps(apiData: CardMapperJrpc.ApiData): CardModel.Props {
    return {
      brand: this.brand(apiData.card.brand),
      created: this.dateTimeMapper.mapToModel(apiData.created),
      expMonth: apiData.card.exp_month,
      expYear: apiData.card.exp_year,
      id: apiData._id,
      last4: apiData.card.last4,
      type: this.type(apiData.type),
      updated: this.dateTimeMapper.mapToModel(apiData.updated),
    };
  }

  private brand(
    brand: CardMapperJrpc.ApiData['card']['brand'],
  ): CardModel.Brand {
    switch (brand.toLowerCase()) {
      case 'american express':
        return CardModel.Brand.AMERICAN_EXPRESS;
      case 'diners club':
        return CardModel.Brand.DINERS_CLUB;
      case 'discover':
        return CardModel.Brand.DISCOVER;
      case 'eftpos australia':
        return CardModel.Brand.EFTPOS_AUSTRALIA;
      case 'jcb':
        return CardModel.Brand.JCB;
      case 'mastercard':
        return CardModel.Brand.MASTERCARD;
      case 'unionpay':
        return CardModel.Brand.UNIONPAY;
      case 'visa':
        return CardModel.Brand.VISA;
      default:
        return CardModel.Brand.UNKNOWN;
    }
  }

  private type(type: CardMapperJrpc.ApiData['type']): CardModel.Type {
    switch (type.toLowerCase()) {
      case 'card':
        return CardModel.Type.CARD;
      case 'setup_intent':
        return CardModel.Type.INTENT;
      default:
        return CardModel.Type.CARD;
    }
  }
}

export namespace CardMapperJrpc {
  export type ApiData = CardApiModel;

  export type ExpandedApiData = ApiData & CardExpandJrpc.ExpandedFields;
}
